import type { EChartsOption } from "echarts";
import { useRef, useState, useContext, useEffect, useMemo } from "react";
import DashboardCardHeader from "./DashboardCardHeader";
import { Box, CircularProgress, Container } from "@mui/material";
import { RopChart } from "../../../main/components/RopChart";
import { DashboardContext } from "../context/DashboardContexProvider";
import { getYearDay } from "../../../main/themes/defaultLayout";
import { MainContext } from "../../../main/context/MainContextProvider";
import {
  Area,
  BaseErrorCard,
  BaseUsinaValue,
  Period,
  getSelectedArea,
} from "../models/dados-indicadores.model";
import { EmptyTable } from "../../clients/components/EmptyState";
import { CardTypesEnum, EquipmentFilter } from "../types";
import { getCardIsLoading } from "./TextCardContent";
import { usinasApi } from "../../../services/api";
import type { Usina } from "../../usinas/types";
import {
  mergePlantaUGsAtMinutesWithUsinaConfig,
  mergePlantaUGsWithUsinaConfig,
  buildSunburstChartNodes,
  type SunburstChartNode,
} from "../helpers/mergePlantaUGsWithUsinaConfig";

export type SunburstChartValueAndDateTime = {
  dateTime: string;
  value: number;
};

export type SunburstData = {
  planta?: SunburstPropsData;
  plantaMinutes?: PlantaMinutesData;
  cardError: BaseErrorCard;
};

export type SunburstChartProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  data: SunburstData;
  cardType: number;
};
type PlantaMinutesData = {
  name?: string;
  uGs?: PlantaUGAtMinutes[];
  potenciaAtivaAtMinutes?: BaseUsinaValue[];
  lastPotenciaAtivaMinute?: number;
  usinaId: number;
  period: Period;
  collectedAt: string;
  errors: any[];
  energiaGerada?: number;
  potenciaAtiva?: number;
};
export type SunburstPropsData = {
  name: string;
  energiaGerada?: BaseUsinaValue;
  potenciaAtiva?: BaseUsinaValue;
  lastCollectTime?: string;
  uGs: PlantaUG[];
  usinaId: number;
  period: Period;
  collectedAt: string;
  errors: any[];
};

export type PlantaUG = {
  name?: string;
  inversores?: PlantaInversor[];
  usinaId?: number;
  energiaGerada?: BaseUsinaValue;
  potenciaAtiva?: BaseUsinaValue;
};
export type PlantaUGAtMinutes = Omit<PlantaUG, "inversores"> & {
  inversores: PlantaInversorAtMinutes[];
  potenciaAtivaAtMinutes: BaseUsinaValue[];
  lastPotenciaAtivaMinute: number;
};
export type PlantaInversorAtMinutes = PlantaInversor & {
  potenciaAtivaAtMinutes?: BaseUsinaValue[];
  lastPotenciaAtivaMinute?: number;
};
export type PlantaInversor = {
  name?: string;
  usinaId?: number;
  energiaGerada?: BaseUsinaValue;
  potenciaAtiva?: BaseUsinaValue;
};

type SunburstChartData = SunburstChartNode[];

function buildEnergiaGeradaChartData(
  plantName: string | undefined,
  planta?: SunburstPropsData,
): SunburstChartData {
  if (!planta) return [];

  const ugNodes = buildSunburstChartNodes(
    (planta.uGs ?? []).map((uG) => ({
      name: uG.name,
      actualValue: uG.energiaGerada?.value,
      children: (uG.inversores ?? []).map((inversor) => ({
        name: inversor.name,
        actualValue: inversor.energiaGerada?.value,
      })),
    })),
  );

  return [
    {
      name: plantName ?? planta.name,
      value: ugNodes.reduce((sum, node) => sum + node.value, 0),
      actualValue: planta.energiaGerada?.value ?? 0,
      children: ugNodes,
    },
  ];
}

function buildPotenciaAtivaChartData(
  plantName: string | undefined,
  plantaMinutes?: PlantaMinutesData,
): SunburstChartData {
  if (!plantaMinutes) return [];

  const ugInputs = (plantaMinutes.uGs ?? []).map((uG) => {
    const inversores = (uG.inversores ?? []).filter(
      (inversor) => (inversor.lastPotenciaAtivaMinute ?? 0) > 0,
    );
    const actualValue = resolvePotenciaAtivaUgValue(uG);

    return {
      name: uG.name,
      actualValue: actualValue ?? 0,
      children:
        inversores.length > 0
          ? inversores.map((inversor) => ({
            name: inversor.name,
            actualValue: inversor.lastPotenciaAtivaMinute,
          }))
          : undefined,
    };
  });

  if (!ugInputs.length) return [];

  const ugNodes = buildSunburstChartNodes(ugInputs);
  const rootValue = ugNodes.reduce((sum, node) => sum + node.value, 0);

  if (rootValue <= 0) return [];

  return [
    {
      name: plantName ?? plantaMinutes.name,
      value: rootValue,
      actualValue: plantaMinutes.lastPotenciaAtivaMinute ?? 0,
      children: ugNodes,
    },
  ];
}

function resolvePotenciaAtivaUgValue(uG: PlantaUGAtMinutes): number | undefined {
  if (uG.lastPotenciaAtivaMinute != null && uG.lastPotenciaAtivaMinute > 0) {
    return uG.lastPotenciaAtivaMinute;
  }

  const inversoresSum = (uG.inversores ?? []).reduce(
    (sum, inversor) => sum + (inversor.lastPotenciaAtivaMinute ?? 0),
    0,
  );

  return inversoresSum > 0 ? inversoresSum : uG.lastPotenciaAtivaMinute;
}

function getDisplayableSunBurstEnergiaGeradaData(
  equipmentFilter: EquipmentFilter,
  data: any
): SunburstChartData | undefined {
  if (!data) return undefined;
  if (equipmentFilter.ugName && equipmentFilter.inversorName) {
    return buildSunburstChartNodes([
      {
        name: data?.name,
        actualValue: data?.energiaGerada?.value,
      },
    ]);
  }
  if (equipmentFilter.ugName && !equipmentFilter.inversorName) {
    const children = buildSunburstChartNodes(
      (data.inversores ?? []).map((inversor: any) => ({
        name: inversor.name,
        actualValue: inversor.energiaGerada?.value,
      })),
    );

    return [
      {
        name: data.name,
        value: children.reduce((sum, child) => sum + child.value, 0) || 1,
        actualValue: data.energiaGerada?.value ?? 0,
        children,
      },
    ];
  }
}

function getDisplayableSunBurstPotenciaAtivaData(
  equipmentFilter: EquipmentFilter,
  data: any
): SunburstChartData | undefined {
  if (!data) return undefined;
  if (equipmentFilter.ugName && equipmentFilter.inversorName) {
    return buildSunburstChartNodes([
      {
        name: data?.name,
        actualValue: data?.lastPotenciaAtivaMinute,
      },
    ]);
  }
  if (equipmentFilter.ugName && !equipmentFilter.inversorName) {
    const children = buildSunburstChartNodes(
      (data.inversores ?? []).map((inversor: any) => ({
        name: inversor.name,
        actualValue: inversor.lastPotenciaAtivaMinute,
      })),
    );

    return [
      {
        name: data.name,
        value: children.reduce((sum, child) => sum + child.value, 0) || 1,
        actualValue: data?.lastPotenciaAtivaMinute ?? 0,
        children,
      },
    ];
  }
}

function hasRenderableSunburstData(data: SunburstChartData): boolean {
  if (!data.length) return false;

  const root = data[0];
  if (!root) return false;
  if (root.children?.length) return true;

  return root.value > 0;
}

function resolveSunburstChartDay(...candidates: (string | undefined)[]): string {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const formattedDay = getYearDay(candidate);
    if (formattedDay) return formattedDay;
  }

  return "";
}

function formatSunburstTooltipValue(
  value: number | string | undefined,
  actualValue?: number,
): string {
  const numericValue =
    actualValue ?? (typeof value === "number" ? value : Number(value));
  if (Number.isNaN(numericValue)) return "0,0000";
  return numericValue.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

export default function SunburstChart(props: SunburstChartProps) {
  const { title, data, lastUpdate, cardType } = props;
  const [isMouseIn, setIsMouseIn] = useState(false);
  const {
    showPotenciaAtivaInCharts,
    setShowPotenciaAtivaInCharts,
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    setEquipmentFilter,
    equipmentFilter,
    cardIsLoading,
    requestParamCards
  } = useContext(DashboardContext);

  const {
    selectedDashboardUsina: selectedUsina
  } = useContext(MainContext);

  const [currentUsina, setCurrentUsina] = useState<Usina>();

  useEffect(() => {
    let isMounted = true;

    const loadUsinaConfig = async () => {
      if (!selectedUsina?.usinaIds.id) {
        setCurrentUsina(undefined);
        return;
      }

      try {
        const usinaResponse = await usinasApi.getUsina(selectedUsina.usinaIds.id);
        if (isMounted) {
          setCurrentUsina(usinaResponse);
        }
      } catch (error) {
        console.error("Erro ao carregar configuração da usina para SunBurstChart", error);
        if (isMounted) {
          setCurrentUsina(undefined);
        }
      }
    };

    loadUsinaConfig();

    return () => {
      isMounted = false;
    };
  }, [selectedUsina?.usinaIds.id]);

  const mergedPlanta = useMemo(() => {
    if (!data.planta) return undefined;

    return {
      ...data.planta,
      uGs: mergePlantaUGsWithUsinaConfig(currentUsina?.uGs, data.planta.uGs),
    };
  }, [currentUsina?.uGs, data.planta]);

  const mergedPlantaMinutes = useMemo(() => {
    if (!data.plantaMinutes) return undefined;

    return {
      ...data.plantaMinutes,
      uGs: mergePlantaUGsAtMinutesWithUsinaConfig(
        currentUsina?.uGs,
        data.plantaMinutes.uGs,
      ),
    };
  }, [currentUsina?.uGs, data.plantaMinutes]);

  const [potenciaAtivaColor, setPotenciaAtivaColor] = useState<string>(
    cardsDisplayableColorProps.geracaoPlanta.potenciaAtivaColor
  );
  const [plantaColor, setPlantaColor] = useState<string>(
    cardsDisplayableColorProps.geracaoPlanta.plantaColor
  );
  const [ugColor, setUgColor] = useState<string>(
    cardsDisplayableColorProps.geracaoPlanta.ugColor
  );
  const [inversorColor, setInversorColor] = useState<string>(
    cardsDisplayableColorProps.geracaoPlanta.inversorColor
  );
  const [plantaEnergiaGerada, setPlantaEnergiaGerada] = useState<Area>(
    getSelectedArea(mergedPlanta, equipmentFilter)
  );
  const [plantaPotenciaAtiva, setPlantaPotenciaAtiva] = useState<Area>(
    getSelectedArea(mergedPlantaMinutes, equipmentFilter)
  );

  const { isMobile } = useContext(MainContext);
  const measureUnit = !showPotenciaAtivaInCharts ? "MWh" : "MW";
  const chartData: SunburstChartData = !showPotenciaAtivaInCharts
    ? buildEnergiaGeradaChartData(selectedUsina?.name, mergedPlanta)
    : buildPotenciaAtivaChartData(
      selectedUsina?.name,
      mergedPlantaMinutes,
    );
  const activeChartData = !showPotenciaAtivaInCharts
    ? getDisplayableSunBurstEnergiaGeradaData(equipmentFilter, plantaEnergiaGerada) ?? chartData
    : getDisplayableSunBurstPotenciaAtivaData(equipmentFilter, plantaPotenciaAtiva) ?? chartData;
  const hasActiveChartData = hasRenderableSunburstData(activeChartData);
  const potenciaAtivaDay = resolveSunburstChartDay(
    data.plantaMinutes?.potenciaAtivaAtMinutes?.[0]?.dateTime,
    data.plantaMinutes?.collectedAt,
  );
  const energiaGeradaDay = resolveSunburstChartDay(
    data.planta?.lastCollectTime,
    data.planta?.energiaGerada?.dateTime,
    data.planta?.collectedAt,
  );
  const option: EChartsOption = {
    title: {
      text: !showPotenciaAtivaInCharts ? energiaGeradaDay : potenciaAtivaDay,
      show: Boolean(!showPotenciaAtivaInCharts ? energiaGeradaDay : potenciaAtivaDay),
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "item",
      formatter: function (params: any) {
        const vPtBr = formatSunburstTooltipValue(
          params.value,
          params.data?.actualValue,
        );
        return `${params?.seriesName}<br/> ${params?.name}: ${vPtBr} ${measureUnit}`;
      },
      confine: true,
    },
    backgroundColor: "transparent",

    series: {
      name: !showPotenciaAtivaInCharts ? "Energia Gerada" : "Potência Ativa",
      type: "sunburst",
      label: {
        rotate: "radial",
        fontSize: isMobile ? 6 : 12,
      },
      data: activeChartData,
      radius: ["0%", "90%"],
      sort: "desc",

      levels: [
        {
          itemStyle: {
            color: potenciaAtivaColor,
          },
          label: {
            rotate: 0,
          },
        },
        {
          itemStyle: {
            color: plantaColor,
          },
          label: {
            rotate: 0,
            position: ["50%", "50%"],
            align: "center",
          },
        },
        {
          itemStyle: {
            color: ugColor,
          },
          label: {
            rotate: 0,
            minAngle: 6,
            formatter: "{b}",
          },
        },
        {
          itemStyle: {
            color: inversorColor,
          },
          label: {
            rotate: 0,
            minAngle: 10,
            formatter: "{b}",
          },
        },
      ],
    },
  };
  const elementRef = useRef(null);
  const selectedTitle = !showPotenciaAtivaInCharts
    ? "Energia Gerada por Equipamento"
    : "Potência Ativa por Equipamento";
  const headerTitle = title ? selectedTitle : "";
  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;

  const [openModal, setopenModal] = useState<boolean>(false);

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      geracaoPlanta: {
        inversorColor: inversorColor,
        plantaColor: plantaColor,
        potenciaAtivaColor: potenciaAtivaColor,
        ugColor: ugColor,
      },
    });
    setopenModal(false);
  };

  const hasData = data.planta || data.plantaMinutes ? true : false;

  useEffect(() => {
    setEquipmentFilter({});
  }, [showPotenciaAtivaInCharts, setEquipmentFilter]);

  useEffect(() => {
    if (mergedPlanta) {
      setPlantaEnergiaGerada(getSelectedArea(mergedPlanta, equipmentFilter));
    }
    if (mergedPlantaMinutes) {
      setPlantaPotenciaAtiva(getSelectedArea(mergedPlantaMinutes, equipmentFilter));
    }
  }, [equipmentFilter, mergedPlanta, mergedPlantaMinutes]);

  const handleChartClick = (params: any) => {
    if (params.data.name.includes("INV") && params.treePathInfo.length === 3) {
      setEquipmentFilter({
        ugName: params.treePathInfo[1].name,
        inversorName: params.treePathInfo[2].name,
      });
      return;
    }
    if (params.treePathInfo.length === 3) {
      setEquipmentFilter({
        ugName: params.treePathInfo[2].name,
        inversorName: undefined,
      });
      return;
    }
    if (params.treePathInfo.length === 4) {
      setEquipmentFilter({
        ugName: params.treePathInfo[2].name,
        inversorName: params.treePathInfo[3].name,
      });
      return;
    }
    setEquipmentFilter({});
  };

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        ref={elementRef}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          elementRef={elementRef}
          isMouseIn={isMouseIn}
          cardId={props.cardId}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          onConfirmColorsModal={handleConfirmModal}
          colorButton
          hasEquipmentFilter
          colorsOptions={[
            {
              title: !showPotenciaAtivaInCharts
                ? "Energia Gerada"
                : "Potência Ativa",
              value: potenciaAtivaColor,
              setValue: setPotenciaAtivaColor,
            },
            {
              title: "Planta",
              value: plantaColor,
              setValue: setPlantaColor,
            },
            {
              title: "UG",
              value: ugColor,
              setValue: setUgColor,
            },
            {
              title: "Inversor",
              value: inversorColor,
              setValue: setInversorColor,
            },
          ]}
          toggleItems={{
            disabledLabel: "Energia Gerada",
            enabledLabel: "Potência Ativa",
            toggleValue: showPotenciaAtivaInCharts,
            setToggleValue: setShowPotenciaAtivaInCharts,
          }}
          cardType={CardTypesEnum.GeracaoPlanta}
          hasFilter
        />
        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData && hasActiveChartData ? (
            <Container
              ref={elementRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "90%",
              }}
            >
              <RopChart
                key={showPotenciaAtivaInCharts ? "potencia-ativa" : "energia-gerada"}
                onClick={handleChartClick}
                option={option}
              />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable
                title={
                  showPotenciaAtivaInCharts
                    ? "Nenhum dado de potência ativa encontrado!"
                    : "Nenhum dado encontrado!"
                }
              />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </>
  );
}
